import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "schoolSelection", 
    "schoolSelect", 
    "magicTypeSelection",
    "magicTypeSelect",
    "abilitiesList", 
    "selectedSpells",
    "spiritGrid",
    "infusionSelection",
    "pointsRemaining", 
    "form"
  ]
  static values = {
    priority: String,
    forcePoints: Number,
    selectedSpells: { type: Array, default: [] },
    initialSpells: { type: Array, default: [] },
    initialMagicType: String,
    initialSchool: String,
    maxPoints: Number
  }

  connect() {
    if (this.hasMagicTypeSelectTarget && this.initialMagicTypeValue) {
      this.magicTypeSelectTarget.value = this.initialMagicTypeValue
    }

    // Set initial force points based on magic type
    this.initializeForcePoints()
    
    // Initialize selected spells from initial value
    this.selectedSpellsValue = this.sanitizeInitialSelections(this.initialSpellsValue || [])
    this.refreshUI()
    
    const magicChoiceVisible = this.hasMagicTypeSelectionTarget && !this.magicTypeSelectionTarget.classList.contains('hidden')

    // Show appropriate selection UI based on magic type
    if (magicChoiceVisible) {
      this.schoolSelectionTarget.classList.add('hidden')
      this.magicTypeSelectionTarget.classList.remove('hidden')
      this.handleMagicTypeChange({ preserveSelections: true })
    } else if (this.priorityValue?.toLowerCase() === 'mage') {
      this.schoolSelectionTarget.classList.remove('hidden')
      this.magicTypeSelectionTarget.classList.add('hidden')
      this.spiritGridTarget.classList.add('hidden')
      this.infusionSelectionTarget.classList.add('hidden')
      
      // If we have an initial school value, select it
      if (this.initialSchoolValue) {
        this.schoolSelectTarget.value = this.initialSchoolValue
        this.handleSchoolChange()
      }
    } else if (this.priorityValue?.toLowerCase() === 'archmage') {
      // Archmage can select spells from any school, amplifications, and spirit relationships
      this.schoolSelectionTarget.classList.add('hidden')
      this.magicTypeSelectionTarget.classList.add('hidden')
      this.spiritGridTarget.classList.remove('hidden')
      this.infusionSelectionTarget.classList.add('hidden')
      this.abilitiesListTarget.classList.remove('hidden')
      
      // Load both spells and amplifications for Archmage
      this.loadArchmageOptions()
    } else if (this.priorityValue?.toLowerCase() === 'amp/speaker') {
      this.schoolSelectionTarget.classList.add('hidden')
      this.magicTypeSelectionTarget.classList.remove('hidden')
      this.handleMagicTypeChange({ preserveSelections: true })
    }

    // Add form submission handler
    this.formTarget.addEventListener('submit', (event) => {
      // Update form fields before submission
      this.updateFormFields()
      
      // Validate spell selection
      const errors = this.validateSpellSelection()
      if (errors.length > 0) {
        event.preventDefault()
        alert(errors.join('\n'))
        return
      }
    })
  }

  initializeForcePoints() {
    const magicType = this.currentMagicType()
    
    switch (magicType) {
      case 'archmage':
        this.forcePointsValue = 35
        break
      case 'mage':
        this.forcePointsValue = 25
        break
      case 'speaker':
        this.forcePointsValue = 10
        break
      case 'amp':
        // For amp/speaker, use the force-points-value from the data attribute (character's Zoetic Potential)
        // This should already be set by the data attribute, so we preserve it
        break
      default:
        // If priorityValue is blank but we have a force-points-value data attribute, preserve it
        // This handles the case where a character hasn't set priority_magic yet but we want to show their ZP
        if (!this.hasForcePointsValue || this.forcePointsValue === undefined) {
          this.forcePointsValue = 0
        }
        // Otherwise, keep the existing forcePointsValue from the data attribute
    }

    this.updatePointsRemaining()
  }

  currentMagicType() {
    const selectedMagicType = this.hasMagicTypeSelectTarget ? this.magicTypeSelectTarget.value?.toLowerCase() : null
    if (selectedMagicType) return selectedMagicType

    if (this.priorityValue?.toLowerCase() === 'amp/speaker' && this.initialMagicTypeValue) {
      return this.initialMagicTypeValue.toLowerCase()
    }

    return this.priorityValue?.toLowerCase()
  }

  syncMagicTypeField() {
    const visibleSelect = this.hasMagicTypeSelectTarget ? this.magicTypeSelectTarget : null
    const hiddenField = this.formTarget.querySelector('input[type="hidden"][name="character[magic_type]"]')

    if (hiddenField && visibleSelect) {
      hiddenField.value = visibleSelect.value || ''
    }
  }

  sanitizeInitialSelections(items) {
    if (!Array.isArray(items)) return []

    const normalizedItems = items
      .map(item => this.normalizeInitialSelection(item))
      .filter(Boolean)

    const seen = new Set()
    return normalizedItems.filter(item => {
      const key = this.selectionIdentityKey(item)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  normalizeInitialSelection(item) {
    if (!item || typeof item !== 'object') return null

    const type = item.type || (item.zoetic_cost !== undefined ? 'amplification' : item.force !== undefined ? 'spell' : null)
    if (!type) return null

    switch (type) {
      case 'spell':
        if (item.id === undefined || item.force === undefined) return null
        return {
          id: Number(item.id),
          name: item.name,
          force: Number(item.force),
          type: 'spell'
        }
      case 'amplification':
        if (item.id === undefined || item.zoetic_cost === undefined) return null
        return {
          id: Number(item.id),
          name: item.name,
          zoetic_cost: Number(item.zoetic_cost),
          type: 'amplification',
          selection_uid: item.selection_uid,
          target_attribute: item.target_attribute
        }
      case 'spirit':
        return this.normalizeSpeakerSelection(item, 'spirit')
      case 'infusion':
        return this.normalizeSpeakerSelection(item, 'infusion')
      case 'bond_slot':
        return this.normalizeSpeakerSelection(item, 'bond_slot')
      default:
        return null
    }
  }

  normalizeSpeakerSelection(item, expectedType) {
    if (item.id !== undefined && item.name && item.points !== undefined && Number.isFinite(Number(item.points))) {
      return {
        id: String(item.id),
        name: item.name,
        points: Number(item.points),
        type: expectedType
      }
    }
    return null
  }

  getForcePointsForPriority(priority) {
    const magicType = priority?.toLowerCase()
    
    switch (magicType) {
      case 'archmage':
        return 35
      case 'mage':
        return 25
      case 'speaker':
        return 10
      case 'amp/speaker':
        // For amp/speaker, use the data attribute value (character's Zoetic Potential)
        return this.forcePointsValue || 0
      default:
        // If priority is blank but we have forcePointsValue, return it (for characters without priority_magic set)
        return this.forcePointsValue || 0
    }
  }

  handleSchoolChange() {
    const school = this.schoolSelectTarget.value
    if (school) {
      this.loadSpellList(school)
    }
  }

  handleMagicTypeChange(options = {}) {
    const magicType = this.magicTypeSelectTarget.value?.toLowerCase()
    const preserveSelections = options?.preserveSelections === true

    this.syncMagicTypeField()

    if (!preserveSelections) {
      this.selectedSpellsValue = []
    }

    if (magicType === "speaker") {
      this.schoolSelectionTarget.classList.add("hidden")
      this.spiritGridTarget.classList.remove("hidden")
      this.infusionSelectionTarget.classList.remove("hidden")
      this.abilitiesListTarget.classList.add("hidden")
    } else if (magicType === "amp") {
      this.schoolSelectionTarget.classList.add("hidden")
      this.spiritGridTarget.classList.add("hidden")
      this.infusionSelectionTarget.classList.add("hidden")
      this.abilitiesListTarget.classList.remove("hidden")
      this.loadAbilitiesList("amp")
    } else if (magicType === "mage") {
      this.schoolSelectionTarget.classList.remove("hidden")
      this.spiritGridTarget.classList.add("hidden")
      this.infusionSelectionTarget.classList.add("hidden")
      this.abilitiesListTarget.classList.remove("hidden")

      if (this.schoolSelectTarget.value) {
        this.handleSchoolChange()
      } else {
        this.abilitiesListTarget.innerHTML = ''
      }
    } else if (magicType === "archmage") {
      this.schoolSelectionTarget.classList.add("hidden")
      this.spiritGridTarget.classList.remove("hidden")
      this.infusionSelectionTarget.classList.add("hidden")
      this.abilitiesListTarget.classList.remove("hidden")
      this.loadArchmageOptions()
    } else {
      this.schoolSelectionTarget.classList.add("hidden")
      this.spiritGridTarget.classList.add("hidden")
      this.infusionSelectionTarget.classList.add("hidden")
      this.abilitiesListTarget.classList.add("hidden")
    }

    this.refreshUI()
  }

  updatePointsRemaining() {
    const magicType = this.currentMagicType();
    const currentSelectedSpells = this.selectedSpellsValue; // Use Stimulus value directly

    if (magicType === "speaker") {
      const selectedInfusion = currentSelectedSpells.find(item => item.type === "infusion")
      const spiritPoints = currentSelectedSpells
        .filter(item => item.type === "spirit")
        .reduce((sum, item) => sum + (Number(item.points) || 0), 0);
      const maxPoints = 11;
      const remaining = maxPoints - spiritPoints;
      const infusionStatus = selectedInfusion ? `${selectedInfusion.name || 'Infusion'} selected (10 / 10 infusion points)` : 'No infusion selected (0 / 10 infusion points)'
      this.pointsRemainingTarget.innerHTML = `
        <div>Infusion: ${infusionStatus}</div>
        <div class="mt-1">Spirit Relationship Points Remaining: ${remaining} / ${maxPoints}</div>
      `;
      this.pointsRemainingTarget.classList.toggle('text-red-500', remaining < 0);
    } else {
      let totalPoints = 0;
      // Use maxPointsValue if provided, otherwise derive from priority
      let maxPoints = this.hasMaxPointsValue ? this.maxPointsValue : this.getForcePointsForPriority(this.priorityValue);

      if (magicType === 'amp') {
        totalPoints = currentSelectedSpells.reduce((sum, ability) => {
          return sum + (Number(ability.zoetic_cost) || 0);
        }, 0);
        maxPoints = this.forcePointsValue; 
      } else { // Mage or Archmage
        // Calculate total force points from currentSelectedSpells
        totalPoints = currentSelectedSpells.reduce((sum, item) => {
          if (item.type === 'amplification') {
            // Amplifications use zoetic_cost as force points for Archmage
            return sum + (Number(item.zoetic_cost) || 0);
          } else if (item.force) {
            // Regular spells use force
            return sum + (Number(item.force) || 0);
          } else if (item.points !== undefined) {
            // Spirit relationships and bond-slot unlocks cost force points for Archmage
            return sum + (Number(item.points) || 0);
          }
          return sum;
        }, 0);
        // Ensure maxPoints is correctly assigned if not explicitly set
        if (!this.hasMaxPointsValue) { 
           maxPoints = this.getForcePointsForPriority(this.priorityValue);
        }
      }
      
      const remaining = maxPoints - totalPoints;
      const label = magicType === 'amp' ? 'Zoetic Potential' : 
                   magicType === 'speaker' ? 'Spirit Points' : // Should not hit here due to outer if, but safe
                   'Force Points'; // Default to Force Points for Mage/Archmage
      
      // Check for NaN before displaying
      const displayRemaining = isNaN(remaining) ? 'Error' : remaining;
      const displayMax = isNaN(maxPoints) ? 'N/A' : maxPoints;
      this.pointsRemainingTarget.textContent = `${label} Remaining: ${displayRemaining} / ${displayMax}`;
      this.pointsRemainingTarget.classList.toggle('text-red-500', remaining < 0 || isNaN(remaining));
    }
  }

  async loadSpellList(school) {
    try {
      const response = await fetch(`/api/spells/${encodeURIComponent(school)}`)
      if (!response.ok) throw new Error('Failed to load spells')
      
      const spells = await response.json()
      this.renderSpellList(spells)
    } catch (error) {
      console.error('Error loading spells:', error)
      this.abilitiesListTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-red-500 dark:text-red-400">Error loading spells. Please try again.</p>
        </div>
      `
    }
  }

  async loadAbilitiesList(type) {
    try {
      const response = await fetch(`/api/abilities/${encodeURIComponent(type)}`)
      if (!response.ok) throw new Error('Failed to load abilities')
      
      const abilities = await response.json()
      this.renderAbilitiesList(abilities, type)
    } catch (error) {
      console.error('Error loading abilities:', error)
      this.abilitiesListTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-red-500 dark:text-red-400">Error loading abilities. Please try again.</p>
        </div>
      `
    }
  }

  async loadArchmageOptions() {
    try {
      // Load both spells and amplifications for Archmage
      const [spellsResponse, abilitiesResponse] = await Promise.all([
        fetch('/api/spells/all'),
        fetch('/api/abilities/archmage')
      ])
      
      if (!spellsResponse.ok || !abilitiesResponse.ok) {
        throw new Error('Failed to load archmage options')
      }
      
      const spells = await spellsResponse.json()
      const abilities = await abilitiesResponse.json()
      
      this.renderArchmageOptions(spells, abilities)
    } catch (error) {
      console.error('Error loading archmage options:', error)
      this.abilitiesListTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-red-500 dark:text-red-400">Error loading archmage options. Please try again.</p>
        </div>
      `
    }
  }

  renderSpellList(spells) {
    if (!spells || spells.length === 0) {
      this.abilitiesListTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-gray-500 dark:text-gray-400">No spells available for this school.</p>
        </div>
      `
      return
    }

    this.abilitiesListTarget.innerHTML = `
      <div class="space-y-4">
        ${spells.map(spell => this.renderSpellCard(spell)).join('')}
      </div>
    `
  }

  renderAbilitiesList(abilities, type) {
    if (!abilities || abilities.length === 0) {
      this.abilitiesListTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-gray-500 dark:text-gray-400">No abilities available for ${type}.</p>
        </div>
      `
      return
    }

    // Clear the existing content
    this.abilitiesListTarget.innerHTML = ''
    
    // Create a container for the abilities
    const container = document.createElement('div')
    container.className = 'space-y-4'
    
    // Add each ability card to the container
    abilities.forEach(ability => {
      const card = this.renderAbilityCard(ability)
      container.appendChild(card)
    })
    
    // Replace the content with the new container
    this.abilitiesListTarget.appendChild(container)
  }

  renderArchmageOptions(spells, abilities) {
    // Clear the existing content
    this.abilitiesListTarget.innerHTML = ''
    
    // Create main container
    const mainContainer = document.createElement('div')
    mainContainer.className = 'space-y-6'
    
    // Spells section
    if (spells && spells.length > 0) {
      const spellsSection = document.createElement('div')
      spellsSection.innerHTML = `
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Spells (From Any School)</h3>
        <div class="space-y-4">
          ${spells.map(spell => this.renderSpellCard(spell)).join('')}
        </div>
      `
      mainContainer.appendChild(spellsSection)
    }
    
    // Amplifications section
    if (abilities && abilities.length > 0) {
      const abilitiesSection = document.createElement('div')
      abilitiesSection.className = 'mt-6'
      
      const abilitiesTitle = document.createElement('h3')
      abilitiesTitle.className = 'text-lg font-semibold text-gray-900 dark:text-white mb-4'
      abilitiesTitle.textContent = 'Amplifications (Full Zoetic Potential Cost)'
      abilitiesSection.appendChild(abilitiesTitle)
      
      const abilitiesContainer = document.createElement('div')
      abilitiesContainer.className = 'space-y-4'
      
      abilities.forEach(ability => {
        const card = this.renderArchmageAbilityCard(ability)
        abilitiesContainer.appendChild(card)
      })
      
      abilitiesSection.appendChild(abilitiesContainer)
      mainContainer.appendChild(abilitiesSection)
    }
    
    this.abilitiesListTarget.appendChild(mainContainer)
  }

  // Helper method to safely encode spell data
  encodeSpellData(obj) {
    try {
      const jsonString = JSON.stringify(obj)
      return btoa(encodeURIComponent(jsonString))
    } catch (e) {
      console.error('Failed to encode spell data:', e)
      return btoa(JSON.stringify({ 
        id: obj.id, 
        name: obj.name || 'Error: Invalid spell data',
        price: obj.price || 0,
        max_force: obj.max_force || 1
      }))
    }
  }

  // Helper method to safely decode spell data
  decodeSpellData(str) {
    try {
      return JSON.parse(decodeURIComponent(atob(str)))
    } catch (e) {
      console.error('Failed to decode spell data:', e)
      return null
    }
  }

  renderSpellCard(spell) {
    const currentSelectedSpells = this.selectedSpellsValue;
    const isSelected = currentSelectedSpells.some(s => s.id === spell.id);
    const selectedSpellData = currentSelectedSpells.find(s => s.id === spell.id);
    
    // Create a clean version of the spell data
    const spellDataForAttr = {
      id: spell.id,
      name: spell.name, // Ensure name is included
      price: spell.price,
      max_force: spell.max_force,
      duration: spell.duration,
      drain: spell.drain,
      target_resistance: spell.target_resistance,
      description: spell.description
    };

    // Safely encode the cleaned data
    const encodedSpellData = this.encodeSpellData(spellDataForAttr);

    // Generate force options
    const forceOptions = Array.from({ length: spell.max_force }, (_, i) => i + 1)
      .map(force => `<option value="${force}" ${selectedSpellData && selectedSpellData.force === force ? 'selected' : ''}>${force}</option>`)
      .join('');

    return `
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4" data-spell-id="${spell.id}">
        <div class="flex justify-between items-start">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">${spell.name || 'Unnamed Spell'}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">Drain: ${spell.drain || 'N/A'} | Target: ${spell.target_resistance || 'N/A'} | Duration: ${spell.duration || 'N/A'}</p>
            <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">${spell.description || 'No description available.'}</p>
          </div>
          <div class="ml-4 flex-shrink-0">
            ${isSelected ? `
              <div class="text-right">
                 <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Selected Force: ${selectedSpellData.force}</p>
                 <button type="button" 
                         data-action="click->magic-selection#removeSpell" 
                         data-spell-id="${spell.id}"
                         class="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">
                   Remove
                 </button>
              </div>
            ` : `
              <div class="flex items-center space-x-2">
                <select data-magic-selection-target="forceSelect" name="force" 
                        class="block w-20 pl-2 pr-6 py-1 text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500">
                  ${forceOptions}
                </select>
                <button type="button" 
                        data-action="click->magic-selection#addSpell" 
                        data-spell-data="${encodedSpellData}"
                        class="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  Select
                </button>
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  }

  renderAbilityCard(ability) {
    const selectedCount = this.selectedAbilityCount(ability.id, 'amplification')
    const isRepeatable = this.isRepeatableAmplification(ability.id)
    const isSelected = selectedCount > 0
    const currentType = this.magicTypeSelectTarget.value
    const pointsLabel = currentType === 'amp' ? 'Zoetic Cost' : 'Spirit Points'
    const pointsValue = currentType === 'amp' ? ability.zoetic_cost : ability.points
    
    const card = document.createElement('div')
    card.className = `bg-white dark:bg-gray-700 rounded-lg shadow p-4 ${isSelected ? 'border-2 border-blue-500' : ''}`
    
    // Header section with name and points
    const header = document.createElement('div')
    header.className = 'flex justify-between items-start mb-4'
    
    const titleDiv = document.createElement('div')
    const title = document.createElement('h3')
    title.className = 'text-lg font-semibold text-gray-900 dark:text-white'
    title.textContent = ability.name
    titleDiv.appendChild(title)
    
    const points = document.createElement('p')
    points.className = 'text-sm text-gray-600 dark:text-gray-400'
    points.textContent = `${pointsLabel}: ${pointsValue}`
    titleDiv.appendChild(points)
    
    header.appendChild(titleDiv)
    
    // Description section
    const description = document.createElement('p')
    description.className = 'mt-2 text-sm text-gray-700 dark:text-gray-300'
    description.textContent = ability.description || ''
    
    // Button section
    const buttonContainer = document.createElement('div')
    buttonContainer.className = 'mt-3 flex justify-end items-center space-x-2'

    if (this.requiresAttributeTarget(ability.id)) {
      const targetSelect = this.buildAttributeTargetSelect()
      buttonContainer.appendChild(targetSelect)
    }

    if (isRepeatable && selectedCount > 0) {
      const countLabel = document.createElement('span')
      countLabel.className = 'text-xs text-gray-600 dark:text-gray-300'
      countLabel.textContent = `${selectedCount} selected`
      buttonContainer.appendChild(countLabel)
    }
    
    const button = document.createElement('button')
    if (isSelected && !isRepeatable) {
      button.className = 'px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm'
      button.textContent = 'Remove'
      button.dataset.action = 'magic-selection#removeAbility'
    } else {
      button.className = 'px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm'
      button.textContent = 'Add'
      button.dataset.action = 'magic-selection#addAbility'
    }
    button.dataset.abilityId = ability.id
    button.dataset.abilityDetails = this.encodeSpellData(ability)
    
    buttonContainer.appendChild(button)
    
    // Assemble the card
    card.appendChild(header)
    card.appendChild(description)
    card.appendChild(buttonContainer)
    
    return card
  }

  renderArchmageAbilityCard(ability) {
    const currentSelectedSpells = this.selectedSpellsValue
    const selectedCount = this.selectedAbilityCount(ability.id, 'amplification')
    const isRepeatable = this.isRepeatableAmplification(ability.id)
    const isSelected = currentSelectedSpells.some(selected => selected.id === ability.id && selected.type === 'amplification')
    
    const card = document.createElement('div')
    card.className = `bg-white dark:bg-gray-700 rounded-lg shadow p-4 ${isSelected ? 'border-2 border-blue-500' : ''}`
    
    // Header section with name and cost
    const header = document.createElement('div')
    header.className = 'flex justify-between items-start mb-4'
    
    const titleDiv = document.createElement('div')
    const title = document.createElement('h3')
    title.className = 'text-lg font-semibold text-gray-900 dark:text-white'
    title.textContent = ability.name
    titleDiv.appendChild(title)
    
    const cost = document.createElement('p')
    cost.className = 'text-sm text-gray-600 dark:text-gray-400'
    cost.textContent = `Force Points: ${ability.zoetic_cost}`
    titleDiv.appendChild(cost)
    
    header.appendChild(titleDiv)
    
    // Description section
    const description = document.createElement('p')
    description.className = 'mt-2 text-sm text-gray-700 dark:text-gray-300'
    description.textContent = ability.description || ''
    
    // Button section
    const buttonContainer = document.createElement('div')
    buttonContainer.className = 'mt-3 flex justify-end items-center space-x-2'

    if (this.requiresAttributeTarget(ability.id)) {
      const targetSelect = this.buildAttributeTargetSelect()
      buttonContainer.appendChild(targetSelect)
    }

    if (isRepeatable && selectedCount > 0) {
      const countLabel = document.createElement('span')
      countLabel.className = 'text-xs text-gray-600 dark:text-gray-300'
      countLabel.textContent = `${selectedCount} selected`
      buttonContainer.appendChild(countLabel)
    }
    
    const button = document.createElement('button')
    if (isSelected && !isRepeatable) {
      button.className = 'px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm'
      button.textContent = 'Remove'
      button.dataset.action = 'magic-selection#removeArchmageAbility'
    } else {
      button.className = 'px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm'
      button.textContent = 'Add'
      button.dataset.action = 'magic-selection#addArchmageAbility'
    }
    button.dataset.abilityId = ability.id
    button.dataset.abilityDetails = this.encodeSpellData(ability)
    
    buttonContainer.appendChild(button)
    
    // Assemble the card
    card.appendChild(header)
    card.appendChild(description)
    card.appendChild(buttonContainer)
    
    return card
  }

  addSpell(event) {
    const button = event.currentTarget;
    const spellCard = button.closest('[data-spell-id]'); // Find the parent spell card div
    if (!spellCard) {
      console.error("Could not find parent spell card for button:", button);
      return;
    }
    
    // Find the forceSelect within this specific card
    const forceSelect = spellCard.querySelector('[data-magic-selection-target="forceSelect"]');
    if (!forceSelect) {
      console.error("Could not find force select dropdown within the spell card:", spellCard);
      return;
    }

    const selectedForce = parseInt(forceSelect.value, 10); // Parse force as integer
    if (isNaN(selectedForce)) {
       console.error("Invalid force selected:", forceSelect.value);
       // Optionally provide user feedback
       return; 
    }

    const encodedSpellData = button.dataset.spellData;
    const spellData = this.decodeSpellData(encodedSpellData);

    if (!spellData || !spellData.id || !spellData.name) {
      console.error('Failed to decode spell data or data is incomplete', encodedSpellData);
      // Provide feedback to user?
      return;
    }

    // Add spell using centralized method
    const newSpell = { 
        id: spellData.id, 
        name: spellData.name,
        force: selectedForce
    };
    
    this.addSelectedItem(newSpell); 
  }

  removeSpell(event) {
    const spellIdToRemove = parseInt(event.currentTarget.dataset.spellId, 10);
    if (isNaN(spellIdToRemove)) {
        console.error("Invalid spell ID for removal:", event.currentTarget.dataset.spellId);
        return;
    }

    this.removeSelectedItem(spellIdToRemove);
  }

  updateSelectedSpells() {
    const currentSelectedSpells = this.selectedSpellsValue; // Use Stimulus value directly
    this.selectedSpellsTarget.innerHTML = ''; // Clear existing content

    if (currentSelectedSpells.length === 0) {
      this.selectedSpellsTarget.innerHTML = '<p class="text-sm text-gray-500 dark:text-gray-400">No spells selected.</p>';
    } else {
      currentSelectedSpells.forEach(item => {
        const itemElement = document.createElement('div');
        itemElement.className = 'flex justify-between items-center text-sm py-1';

        let displayText = '';
        let removeAction = '';
        
        if (item.type === 'amplification' || item.zoetic_cost !== undefined) {
          const targetLabel = item.target_attribute ? ` - ${this.formatAttributeLabel(item.target_attribute)}` : ''
          displayText = `${item.name || 'Unnamed Amplification'}${targetLabel} (${item.zoetic_cost} Zoetic Cost)`;
          removeAction = 'click->magic-selection#removeSelectedMagicItem';
        } else if (item.type === 'spirit') {
          displayText = `${item.name || 'Unnamed Spirit'} (${item.points} Points)`;
          removeAction = 'click->magic-selection#removeSelectedMagicItem';
        } else if (item.type === 'infusion') {
          displayText = `${item.name || 'Unnamed Infusion'} (${item.points} Points)`;
          removeAction = 'click->magic-selection#removeSelectedMagicItem';
        } else if (item.type === 'bond_slot') {
          displayText = `${item.name || 'Spirit Bond Slot'} (${item.points} Force Points)`;
          removeAction = 'click->magic-selection#removeSelectedMagicItem';
        } else {
          displayText = `${item.name || 'Unnamed Spell'} (Force: ${item.force || 'N/A'})`;
          removeAction = 'click->magic-selection#removeSelectedMagicItem';
        }
        
        itemElement.innerHTML = `
          <span class="text-gray-800 dark:text-gray-200">${displayText}</span>
          <button type="button" 
                  data-action="${removeAction}" 
                  data-spell-id="${item.id}"
                  data-ability-id="${item.id}"
                  data-item-id="${item.id}"
                  data-item-type="${item.type || 'spell'}"
                  data-item-selection-uid="${item.selection_uid || ''}"
                  class="ml-2 text-red-500 hover:text-red-700 text-xs">
            Remove
          </button>
        `;
        this.selectedSpellsTarget.appendChild(itemElement);
      });
    }
    
    // Determine which list needs refreshing based on current state
    this.refreshCurrentAbilitiesView();
    
    // Always update points after selection changes
    this.updatePointsRemaining(); 
  }
  
  // New helper to refresh the visible list (spells or abilities)
  refreshCurrentAbilitiesView() {
    const magicType = this.currentMagicType();
    const school = this.schoolSelectTarget?.value;

    if (magicType === 'amp') {
      this.loadAbilitiesList('amp');
    } else if (magicType === 'mage' && school) {
      this.loadSpellList(school);
    } else if (magicType === 'archmage') {
       // Load both spells and amplifications for archmage
       this.loadArchmageOptions();
    } 
    // Speaker view doesn't use abilitiesListTarget in the same way
  }

  updateFormFields() {
    // Get the current selected spells/abilities
    const spellsToSubmit = this.selectedSpellsValue.map(spell => {
      if (spell.type === 'amplification') {
        const payload = {
          id: spell.id,
          type: 'amplification',
          name: spell.name,
          zoetic_cost: spell.zoetic_cost,
          description: spell.description,
          selection_uid: spell.selection_uid
        };
        if (spell.target_attribute) payload.target_attribute = spell.target_attribute
        return payload
      } else if (spell.type === 'spirit' || spell.type === 'infusion') {
        return {
          id: spell.id,
          type: spell.type,
          name: spell.name,
          points: spell.points
        };
      } else if (spell.type === 'bond_slot') {
        return {
          id: spell.id,
          type: 'bond_slot',
          name: spell.name,
          points: spell.points
        };
      } else {
        return {
          id: spell.id,
          type: 'spell',
          name: spell.name,
          force: spell.force
        };
      }
    });

    // Remove any existing selected_spells hidden inputs
    this.formTarget.querySelectorAll('input[name^="character[selected_spells]"]').forEach(input => {
      input.remove();
    });

    // Create a single JSON field instead of multiple indexed fields
    const selectedSpellsInput = document.createElement('input');
    selectedSpellsInput.type = 'hidden';
    selectedSpellsInput.name = 'character[selected_spells]';
    selectedSpellsInput.value = JSON.stringify(spellsToSubmit);
    this.formTarget.appendChild(selectedSpellsInput);
  }



  validateSpellSelection() {
    // Basic client-side check for remaining points
    const errors = [];
    const remainingText = this.pointsRemainingTarget.textContent;
    // Extract the number part carefully
    const match = remainingText.match(/Remaining: ([\-\d.]+)/); 
    if (match && parseFloat(match[1]) < 0) {
      errors.push("You have exceeded the available points.");
    }
    
    // Add other client-side checks if necessary (e.g., mage school consistency)
    const magicType = this.magicTypeSelectTarget?.value?.toLowerCase() || this.priorityValue?.toLowerCase();
    if(magicType === 'mage') {
        const currentSelectedSpells = this.selectedSpellsValue;
        if (currentSelectedSpells.length > 0) {
           // Need spell school data here to validate
           // This requires fetching spell details or including school in selectedSpellsValue
           // **Deferring complex client-side school validation to server-side for now**
           // console.log("Client-side school validation skipped.");
        }
    }

    return errors;
  }

  addAbility(event) {
    event.preventDefault()
    const button = event.currentTarget
    const abilityId = parseInt(button.dataset.abilityId)
    
    // Get ability details directly from the clicked button
    try {
      const abilityDetails = this.decodeSpellData(button.dataset.abilityDetails)
      if (!abilityDetails) {
        throw new Error('Failed to decode ability details')
      }

      const targetAttribute = this.selectedTargetAttributeForButton(button, abilityDetails)
      if (this.requiresAttributeTarget(abilityDetails.id) && !targetAttribute) {
        alert('Choose which attribute this amplification affects.')
        return
      }
      
      console.log('Successfully decoded ability details:', abilityDetails)
      
      // Check if we have enough points
      const currentType = this.magicTypeSelectTarget.value
      const totalPoints = this.selectedSpellsValue.reduce((sum, ability) => {
        const cost = currentType === 'amp' ? ability.zoetic_cost : ability.points
        return sum + cost
      }, 0)
      
      const newCost = currentType === 'amp' ? abilityDetails.zoetic_cost : abilityDetails.points
      const newTotal = totalPoints + newCost
      const maxPoints = currentType === 'amp' ? this.forcePointsValue : 11 // 11 points for speakers
      
      if (newTotal > maxPoints) {
        alert(`Not enough ${currentType === 'amp' ? 'Zoetic Potential' : 'points'} remaining!`)
        return
      }

      console.log('Adding ability with data:', abilityDetails)
      this.addSelectedItem({
        ...abilityDetails,
        type: 'amplification',
        selection_uid: crypto.randomUUID(),
        target_attribute: targetAttribute
      })
    } catch (e) {
      console.error('Failed to parse ability details:', e)
      alert("There was an error adding this ability. Please try another or refresh the page.")
    }
  }

  removeAbility(event) {
    event.preventDefault()
    const abilityId = parseInt(event.currentTarget.dataset.abilityId)
    this.removeSelectedItem(abilityId)
  }

  removeSelectedMagicItem(event) {
    event.preventDefault()
    const itemId = event.currentTarget.dataset.itemId
    const itemType = event.currentTarget.dataset.itemType
    const selectionUid = event.currentTarget.dataset.itemSelectionUid
    this.removeSelectedItem(itemId, itemType === 'spell' ? null : itemType, selectionUid)
  }

  addArchmageAbility(event) {
    event.preventDefault()
    const button = event.currentTarget
    const abilityId = parseInt(button.dataset.abilityId)
    
    try {
      const abilityDetails = this.decodeSpellData(button.dataset.abilityDetails)
      if (!abilityDetails) {
        throw new Error('Failed to decode ability details')
      }

      const targetAttribute = this.selectedTargetAttributeForButton(button, abilityDetails)
      if (this.requiresAttributeTarget(abilityDetails.id) && !targetAttribute) {
        alert('Choose which attribute this amplification affects.')
        return
      }
      
      // Check if we have enough force points
      const currentSelectedSpells = this.selectedSpellsValue
      const totalForcePoints = currentSelectedSpells.reduce((sum, item) => {
        if (item.type === 'amplification') {
          return sum + (Number(item.zoetic_cost) || 0)
        } else if (item.force) {
          return sum + (Number(item.force) || 0)
        }
        return sum
      }, 0)
      
      const newTotal = totalForcePoints + abilityDetails.zoetic_cost
      const maxPoints = 35 // Archmage has 35 force points
      
      if (newTotal > maxPoints) {
        alert('Not enough force points remaining!')
        return
      }

      // Add the amplification with type marker
      const amplificationData = {
        ...abilityDetails,
        type: 'amplification',
        selection_uid: crypto.randomUUID(),
        target_attribute: targetAttribute
      }
      
      this.addSelectedItem(amplificationData)
    } catch (e) {
      console.error('Failed to parse ability details:', e)
      alert("There was an error adding this ability. Please try another or refresh the page.")
    }
  }

  removeArchmageAbility(event) {
    event.preventDefault()
    const abilityId = parseInt(event.currentTarget.dataset.abilityId)
    
    this.removeSelectedItem(abilityId, 'amplification')
  }

  selectSpirit(event) {
    const button = event.currentTarget
    const spiritId = button.dataset.spiritId
    const spiritCost = parseInt(button.dataset.spiritCost)
    const spiritName = button.dataset.spiritName

    if (this.priorityValue?.toLowerCase() === 'archmage') {
      const hasBondSlot = this.selectedSpellsValue.some(item => item.type === 'bond_slot')
      if (!hasBondSlot) {
        alert("Archmages must unlock the first spirit bond slot before selecting spirit relationships.")
        return
      }

      const currentPoints = this.selectedSpellsValue.reduce((total, item) => {
        if (item.type === 'amplification') return total + (Number(item.zoetic_cost) || 0)
        if (item.force) return total + (Number(item.force) || 0)
        if (item.points !== undefined) return total + (Number(item.points) || 0)
        return total
      }, 0)

      if (currentPoints + spiritCost > 35) {
        alert("You cannot exceed 35 force points.")
        return
      }
    } else {
      // Check if we have enough points
      const currentPoints = this.calculateTotalPoints()
      if (currentPoints + spiritCost > 11) {
        alert("You cannot exceed 11 points in spirit relationships.")
        return
      }
    }

    // Add spirit to selected spells
    const spiritData = {
      id: spiritId,
      name: spiritName,
      points: spiritCost,
      type: "spirit"
    }
    
    this.addSelectedItem(spiritData)

    // Update button state
    button.disabled = true
    button.classList.add("opacity-50", "cursor-not-allowed")
  }

  selectBondSlot(event) {
    const button = event.currentTarget
    const slotId = button.dataset.bondSlotId
    const slotCost = parseInt(button.dataset.bondSlotCost, 10)
    const slotName = button.dataset.bondSlotName

    const totalForcePoints = this.selectedSpellsValue.reduce((sum, item) => {
      if (item.type === 'amplification') return sum + (Number(item.zoetic_cost) || 0)
      if (item.force) return sum + (Number(item.force) || 0)
      if (item.points !== undefined) return sum + (Number(item.points) || 0)
      return sum
    }, 0)

    if (totalForcePoints + slotCost > 35) {
      alert("You cannot exceed 35 force points.")
      return
    }

    this.addSelectedItem({
      id: slotId,
      name: slotName,
      points: slotCost,
      type: "bond_slot"
    })
  }

  selectInfusion(event) {
    const button = event.currentTarget
    const infusionId = button.dataset.infusionId
    const infusionCost = parseInt(button.dataset.infusionCost)
    const infusionName = button.dataset.infusionName

    // Check if we already have an infusion selected
    const selectedSpells = this.selectedSpellsValue
    const existingInfusion = selectedSpells.find(spell => spell.type === "infusion")
    if (existingInfusion) {
      alert("You can only select one infusion.")
      return
    }

    // Add infusion to selected spells
    const infusionData = {
      id: infusionId,
      name: infusionName,
      points: infusionCost,
      type: "infusion"
    }
    
    this.addSelectedItem(infusionData)

    // Update all infusion buttons
    this.element.querySelectorAll('[data-action="click->magic-selection#selectInfusion"]').forEach(btn => {
      btn.disabled = true
      btn.classList.add("opacity-50", "cursor-not-allowed")
    })
  }

  calculateTotalPoints() {
    const selectedSpells = this.selectedSpellsValue
    const spiritPoints = selectedSpells
      .filter(spell => spell.type === "spirit")
      .reduce((total, spell) => total + spell.points, 0)
    return spiritPoints
  }

  // Centralized state management methods
  addSelectedItem(item) {
    const currentItems = this.selectedSpellsValue
    const incomingId = String(item.id)
    const allowDuplicate = item.type === 'amplification' && this.isRepeatableAmplification(item.id)
    
    // Check for duplicates (by id and type if specified)
    const isDuplicate = !allowDuplicate && currentItems.some(existing => {
      if (item.type && existing.type) {
        return String(existing.id) === incomingId && existing.type === item.type
      }
      return String(existing.id) === incomingId
    })
    
    if (isDuplicate) {
      console.warn("Attempted to add item that is already selected:", item.id, item.type)
      return false
    }
    
    // Add the item and update state
    this.selectedSpellsValue = [...currentItems, item]
    this.refreshUI()
    return true
  }

  removeSelectedItem(id, type = null, selectionUid = null) {
    const currentItems = this.selectedSpellsValue
    const targetId = String(id)
    const updatedItems = currentItems.filter(item => {
      if (selectionUid) {
        return item.selection_uid !== selectionUid
      }
      if (type) {
        return !(String(item.id) === targetId && item.type === type)
      }
      return String(item.id) !== targetId
    })
    
    this.selectedSpellsValue = updatedItems
    this.refreshUI()
  }

  // Centralized UI update method
  refreshUI() {
    this.updateSelectedSpells()
    this.updatePointsRemaining()
    this.updateSpeakerOptionState()
    // Note: refreshCurrentAbilitiesView is already called by updateSelectedSpells()
  }

  updateSpeakerOptionState() {
    const selectedSpiritIds = new Set(
      this.selectedSpellsValue.filter(item => item.type === 'spirit').map(item => String(item.id))
    )
    const hasInfusion = this.selectedSpellsValue.some(item => item.type === 'infusion')
    const hasBondSlot = this.selectedSpellsValue.some(item => item.type === 'bond_slot')

    this.element.querySelectorAll('[data-spirit-id]').forEach((button) => {
      const isSelected = selectedSpiritIds.has(String(button.dataset.spiritId))
      button.disabled = isSelected
      button.classList.toggle('opacity-50', isSelected)
      button.classList.toggle('cursor-not-allowed', isSelected)
    })

    this.element.querySelectorAll('[data-infusion-id]').forEach((button) => {
      button.disabled = hasInfusion
      button.classList.toggle('opacity-50', hasInfusion)
      button.classList.toggle('cursor-not-allowed', hasInfusion)
    })

    this.element.querySelectorAll('[data-bond-slot-id]').forEach((button) => {
      button.disabled = hasBondSlot
      button.classList.toggle('opacity-50', hasBondSlot)
      button.classList.toggle('cursor-not-allowed', hasBondSlot)
    })
  }

  selectedAbilityCount(id, type) {
    return this.selectedSpellsValue.filter(item => String(item.id) === String(id) && item.type === type).length
  }

  isRepeatableAmplification(id) {
    return ['3', '4', '5'].includes(String(id))
  }

  requiresAttributeTarget(id) {
    return ['4', '5'].includes(String(id))
  }

  attributeTargetOptions() {
    return [
      ['Strength', 'strength'],
      ['Body', 'body'],
      ['Reaction', 'reaction'],
      ['Intelligence', 'intelligence'],
      ['Willpower', 'willpower'],
      ['Charisma', 'charisma']
    ]
  }

  buildAttributeTargetSelect() {
    const targetSelect = document.createElement('select')
    targetSelect.className = 'block w-36 pl-2 pr-6 py-1 text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500'
    targetSelect.dataset.targetAttributeSelect = 'true'
    targetSelect.setAttribute('aria-label', 'Choose attribute')

    const placeholder = document.createElement('option')
    placeholder.value = ''
    placeholder.textContent = 'Choose attribute'
    targetSelect.appendChild(placeholder)

    this.attributeTargetOptions().forEach(([label, value]) => {
      const option = document.createElement('option')
      option.value = value
      option.textContent = label
      targetSelect.appendChild(option)
    })

    return targetSelect
  }

  selectedTargetAttributeForButton(button, abilityDetails) {
    if (!this.requiresAttributeTarget(abilityDetails.id)) return null

    const card = button.closest('.bg-white, .dark\\:bg-gray-700')
    const select = card?.querySelector('[data-target-attribute-select="true"]')
    return select?.value || null
  }

  formatAttributeLabel(attributeName) {
    const match = this.attributeTargetOptions().find(([, value]) => value === attributeName)
    return match ? match[0] : attributeName
  }

  selectionIdentityKey(item) {
    if (item?.type === 'amplification' && this.isRepeatableAmplification(item.id)) {
      return item.selection_uid || `${item.type}:${String(item.id)}:${item.target_attribute || ''}`
    }

    return `${item?.type || 'spell'}:${String(item?.id)}`
  }
}
